import { STEPS, TESTIMONIALS, FAQS } from "@/data/content";
import LeadForm from "@/components/LeadForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export function Process() {
  return (
    <section className="py-20 bg-[#f4f9fd] border-b border-slate-200" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Our Straightforward Process</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            How your water analysis works
          </h2>
          <p className="mt-3 text-slate-600">A clear, pressure-free appointment designed around homeowner questions.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.num} className="bg-white p-6 rounded-2xl border border-slate-200 flex flex-col justify-between" data-testid={`process-step-${i}`}>
              <div>
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-800 text-xs font-bold">{s.num}</span>
                <h3 className="text-base font-bold text-slate-900 mt-4">{s.title}</h3>
                <p className="text-xs text-slate-600 mt-2">{s.text}</p>
              </div>
              <span className="text-[11px] text-slate-600 mt-4">{s.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  return (
    <section className="py-20 bg-white border-b border-slate-200" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Homeowner Experiences</span>
            <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
              What Oklahoma homeowners experience
            </h2>
          </div>
          <p className="text-xs text-slate-500 mt-2 md:mt-0 italic">Sample format — real customer stories published here as they are collected.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="p-6 rounded-2xl bg-[#f8fbff] border border-slate-200 flex flex-col justify-between" data-testid={`testimonial-card-${i}`}>
              <div>
                <div className="text-sky-700 text-3xl leading-none editorial-title">&ldquo;</div>
                <p className="text-sm text-slate-700 leading-relaxed italic mt-2">{t.quote}</p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-200/80">
                <span className="text-sm font-bold text-slate-900 block">{t.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  return (
    <section id="faq" className="py-20 bg-[#f4f9fd] border-b border-slate-200" data-testid="faq-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-800">Homeowner Questions Answered</span>
          <h2 className="editorial-title text-3xl sm:text-4xl text-[#0a1b2a] mt-2 font-normal">
            Frequently asked water questions
          </h2>
          <p className="mt-3 text-slate-600">
            Direct, honest answers about Oklahoma water, testing, system sizing, installation, cost, and
            maintenance — the things homeowners actually ask us.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}
              className="bg-white px-6 rounded-2xl border border-slate-200"
              data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-base font-bold text-slate-900 hover:no-underline py-5"
                data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-5"
                data-testid={`faq-content-${i}`}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section id="contact" className="py-24 bg-[#0a1b2a] text-white relative overflow-hidden" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-300">Ready To Know?</span>
            <h2 className="editorial-title text-3xl sm:text-5xl font-normal text-white mt-2 leading-tight">
              Ready to know what's really in your water?
            </h2>
            <p className="mt-4 text-slate-300 text-base leading-relaxed">
              Schedule your free in-home water analysis today. No sales pitches, no obligation — just
              accurate, transparent facts about the water in your home.
            </p>

            <div className="mt-8 space-y-4 border-t border-slate-800 pt-6">
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                <span>Serving OKC &amp; Tulsa Metro Areas</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                <span>Takes 30–45 minutes at your kitchen sink</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                <span>Know your water. Then make it better.</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <span className="w-2 h-2 rounded-full bg-sky-400"></span>
                <span>Call <a href="tel:+18555915274" className="text-white font-semibold hover:text-sky-300 transition" data-testid="contact-phone-link">855-591-5274</a></span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <LeadForm />
          </div>
        </div>
      </div>
    </section>
  );
}
